/*
  Collection of functions for manipulating/reading vessels.
*/

import { unixToDate } from '../utils/general';
import { makeQueryString } from '../utils/web';
import { apiClient as client } from './base/api_client';
import { PgnData, PgnNumber } from '../core/pgn/types';
import { WarningLevel, WarningType } from '../utils/consts';

export interface Vessel {
  id: number,                     // Identifier
  name: string,                   // End-user supplied label
  imei: string,                   // Device IMEI (secondary identifier)
  is_current_user_owner: boolean, // Is currently signed in user the owner of the vessel
  is_electric: boolean,           // Is the vessel marked as electric
  is_favorite: boolean,           // Is the vessel marked as a favorite
  is_publicly_shared: boolean,    // Is the vessel publicly shared
  user_id: number,                // Id of the owner (NOTE: this field will only be present if the logged-in user is an admin)
  created_at: Date                // Date of when the vessel was created
}

export interface VesselUpdate {
  imei: string                // (of digits only), // eg. '2345345378433'
  name: string,               // eg. 'My boat'
  is_electric: boolean
  is_favorite: boolean
  is_publicly_shared: boolean // is the vessel (and its live data) available to the public
}

interface ApiVessel {
  id: number,                     // Identifier
  name: string,                   // End-user supplied label
  imei: string,                   // Device IMEI (secondary identifier)
  is_current_user_owner: boolean, // Is currently signed in user the owner of the vessel
  is_electric: boolean,           // Is the vessel marked as electric
  is_favorite: boolean,           // Is the vessel marked as a favorite
  is_publicly_shared: boolean,    // Is the vessel publicly shared
  user_id: number,                // Id of the owner (NOTE: this field will only be present if the logged-in user is an admin)
  created_at: number              // Date of when the vessel was created in unix format
}

export interface VesselAnchorZone {
  Radius: number,
  Enabled: boolean
}

export interface Warning {
  SrcName: string,   // NAME of the data source that triggered this warning
	SrcLabel: string,  // Label of the data soruce that triggered this warning
	Type: WarningType, // Warning type
	Lvl: WarningLevel, // Warning level
	T: number,         // Time of occurrance, UNIX time
	Val: number        // Actual value that triggered this warning, if applicable
}

export interface EngineTotalRuntime {
  TotalEngHours: number // Total engine hours in seconds
  SrcName: string       // NAME of the engine data source
}

export interface ActiveSession {
  SessionData: {
    [key in PgnNumber]: {
      DataSources: {
        Data: PgnData[],
        Name: string
      }
    }
  },
  Pos: { // vessel's position
    T: number,   // timestamp of position
    Lat: number, // latitude
    Lon: number, // longitude
    Cog: number  // course over ground
  },
  Online: boolean,        // is the vessel online
  TripCnt: number,        // number of trips of vessel
  TripDist: number,       // distance travelled by vessel (in kilometers)
  TotFuel: number,        // total amount of fuel used by vessel (in liters)
  TOnl: number,           // amount of time vessel has been online (in seconds)
  
  EngTotalRuntime: EngineTotalRuntime[]

  Warnings: Warning[] // warnings raised by vessels
}

export interface GeofencePoint {
  Lat: number,
  Lon: number
}

export interface Geofence {
  Points: GeofencePoint[],
  ID: number,
  Type: "RestrictedInside" | "RestrictedOutside",
  Name: string
}

const mapVessel = (v: ApiVessel): Vessel => {
  const {
    created_at,
    ...rest
  } = v;

  return {
    ...rest,
    created_at: unixToDate(created_at)
  };
}

/*
  Creates a new vessel. Can be used to create vessels for other users (by passing that user's id), but the logged-in user must be an admin.
  Return a Promise containing the newly created vessel (VESSEL_OBJ).

  example: createVessel('12345', 'My vessel').then(({ id, name, imei }) => { ... }).catch(e => { ... })
*/
export async function createVessel(
  imei: string,
  name: string,
  isElectric: boolean = false,
  saveSessions: boolean = true,
  userId: null|number = null
): Promise<Vessel> {
  const data: {
    imei: string,
    name: string,
    is_electric: boolean,
    save_sessions: boolean,
    userId?: number
  } = {
    imei,
    name,
    is_electric: isElectric,
    save_sessions: saveSessions
  };

  // Only include the key if not null
  if (userId != null) {
    data['userId'] = userId;
  }

  const v = await client.post(
    'api/vessels',
    data
  );

  return mapVessel(v);
}

/*
  Updates the given vessel. Only values passed are updated.
  Returns a Promise with the modified vessel as its result.
*/
export async function patchVessel(values: Partial<VesselUpdate>, id: number|string): Promise<Vessel> {
  const v = await client.patch(
    `api/vessels/${id}`, {
      ...values
    }
  );

  return mapVessel(v);
}

/*
  Changes whether the vessel is publicly shared.
  Returns a Promise with the modified vessel as its result.
*/
export async function setIsVesselPublic(isPublic: boolean, vesselId: number|string): Promise<Vessel> {
  const values = { is_publicly_shared: isPublic };

  const v = await patchVessel(values, vesselId);
  return v;
}

/*
  Changes whether the vessel is favorized.
  Returns a Promise with the modified vessel as its result.
*/
export async function setIsVesselFavorite(isFavorite: boolean, vesselId: number|string): Promise<Vessel> {
  const values = { is_favorite: isFavorite };

  const v = await patchVessel(values, vesselId);
  return v;
}

/*
  Gets the display configuration used for this vessel by this app (used on the session details page).
  Returns a Promise containing the configuration originally set by 'saveConfig'.
*/
export function getConfig(vesselId: number|string): Promise<object> {
  return client.get(`api/vessels/${vesselId}/configuration`);
}

/*
  Saves the user defined configuration for the given vessel. Can actually contain anything, since the server only stores this value.
  Returns a Promise containing no data.
*/
export function saveConfig(vesselId: number|string, configuration: object): Promise<void> {
  return client.post(
    `api/vessels/${vesselId}/configuration`, {
      ...configuration
    }
  );
}

/*
  Gets array of the user's vessels. Can load the vessels of other user (by passing the user's id), but the logged-in user must be an admin.
  Returns a Promise with an array of the user's vessels as the result.
*/
export async function getUserVessels(userId: string|number|null = null): Promise<Vessel[]> {
  const userPath = !!userId ? userId : 'me';

  const vessels = await client.get(`api/users/${userPath}/vessels`) as ApiVessel[];

  return vessels.map(mapVessel);
}

/*
  Gets an array of all the vessels.
  Returns a Promise containing an array of vessels.

  NOTE: Only admins may invoke this endpoint!
*/
export async function getVessels(): Promise<Vessel[]> {
  const vessels = await client.get('api/vessels') as ApiVessel[];

  return vessels.map(mapVessel);
}

/*
  Gets the given vessel.
  Returns a Promise containing the requested vessel (VESSEL_OBJ).
*/
export async function getVessel(vesselId: number|string): Promise<Vessel> {
  const v = await client.get(`api/vessels/${vesselId}`) as ApiVessel;

  return mapVessel(v);
}

/*
  Deletes the given vessel from the server.
  Returns a Promise with no result.
*/
export function deleteVessel(id: string|number): Promise<void> {
  return client.delete(`api/vessels/${id}`);
}

/*
  Sends a message to the given vessel. The message can be displayed by some devices (eg. e500).
  Returns a Promise with no result.
*/
export function sendMessage(message: string, vesselId: string|number): Promise<void> {
  return client.put(
    `api/vessels/${vesselId}/message`, {
      msg: message
    }
  );
}

/*
  Resets a vessel's statistics.
  Returns a Promise with no result.
*/
export function resetStatistics(vesselId: string|number): Promise<void> {
  return client.put(
    `api/vessels/${vesselId}/resetstats`
  );
}

/*
  Resets the on-board tracking device.
*/
export function resetTrackingDevice(vesselId: string|number): Promise<void> {
  return client.put(`api/vessels/${vesselId}/reset`, {});
}

/*
  Returns the vessels live data.
*/
export function getLiveData(vesselId: string|number): Promise<ActiveSession> {
  const query = makeQueryString({ vesselID: vesselId });

  return client.get(`api/livedata${query}`);
}

export function getLiveDataBatched(vesselIds: (string|number)[]): Promise<ActiveSession[]> {
  const query = makeQueryString({ vesselIDs: vesselIds.join(',') });

  return client.get(`api/livedata${query}`);
}

export function getGeofences(vesselId: string|number): Promise<Geofence[]> {
  return client.get(`api/vessels/${vesselId}/geofences`);
}

export function updateGeofences(vesselId: string|number, geofences: Geofence[]): Promise<void> {
  return client.put(`api/vessels/${vesselId}/geofences`, geofences);
}

export function deleteGeofences(vesselId: string|number): Promise<void> {
  return client.delete(`api/vessels/${vesselId}/geofences`);
}

export function getAnchorZone(vesselId: string|number): Promise<VesselAnchorZone> {
  return client.get(`api/vessels/${vesselId}/anchor`) as Promise<VesselAnchorZone>;
}

export function updateAnchorZone(vesselId: string|number, radius: number, isEnabled: boolean): Promise<void> {
  const data = {
    Enabled: isEnabled,
    Radius: radius
  };

  return client.put(`api/vessels/${vesselId}/anchor`, data);
}